// @greenlight-spec: 32-knowledge.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown';

// Knowledge bodies can be agent-authored, so Preview output is untrusted.
// react-markdown renders CommonMark with raw HTML disabled by default (no
// rehype-raw here) and sanitizes URLs, so no script or raw-HTML injection
// reaches the DOM. Element styling is mapped to design-system tokens rather
// than a typography plugin the repo doesn't ship.
const COMPONENTS: Components = {
  h1: ({ children }) => <h1 className="mt-4 text-lg font-bold first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="mt-4 text-base font-bold first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-3 text-sm font-semibold first:mt-0">{children}</h3>,
  p: ({ children }) => <p className="my-2 text-sm leading-relaxed first:mt-0">{children}</p>,
  ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-5 text-sm">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-5 text-sm">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="text-[hsl(var(--link))] underline underline-offset-2"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-border pl-3 text-sm text-muted-foreground">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-border" />,
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto whitespace-pre-wrap wrap-anywhere rounded-gl border border-border bg-muted/50 p-3 font-mono text-xs leading-relaxed">
      {children}
    </pre>
  ),
  code: ({ className, children }) => {
    // Fenced blocks carry a `language-*` class and are wrapped by `pre` above;
    // inline code has none, so it gets the pill treatment.
    const isBlock = className?.startsWith('language-') ?? false;
    return isBlock ? (
      <code className={className}>{children}</code>
    ) : (
      <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] wrap-anywhere">
        {children}
      </code>
    );
  },
};

const GL_ASSET = 'gl-asset:';

function Placeholder({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <span className="my-2 inline-flex items-center gap-2 rounded-gl border border-dashed border-border px-2 py-1 text-xs text-muted-foreground">
      {children}
    </span>
  );
}

/** What the view needs of an asset to resolve a `gl-asset:` reference. */
export interface MarkdownViewAsset {
  slug: string;
  description: string;
  previewUrl: string | null;
}

export interface MarkdownViewProps {
  body: string;
  /** Assets on the entry being rendered; resolves `gl-asset:<slug>` references. */
  assets?: readonly MarkdownViewAsset[];
  /** The asset list failed to load — distinct from not having arrived yet. */
  assetsUnavailable?: boolean;
  'data-testid'?: string;
}

/**
 * Keep `gl-asset:` intact for the image renderer below. The default transform
 * drops unknown protocols, which would turn a reference into an empty `src` and
 * render the broken-image frame docs/32 rules out.
 */
function urlTransform(url: string): string {
  return url.startsWith(GL_ASSET) ? url : (defaultUrlTransform(url) ?? '');
}

/**
 * A resolved `gl-asset:` image. The signed URL lives 15 minutes, so a preview
 * left open outlives it; falling back to the placeholder keeps that from
 * painting the broken-image frame docs/32 rules out.
 */
function ResolvedAsset({
  slug,
  src,
  alt,
}: {
  slug: string;
  src: string;
  alt: string;
}): React.JSX.Element {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [src]);

  if (failed) {
    return (
      <Placeholder>
        Preview unavailable: <code className="font-mono">{slug}</code>
      </Placeholder>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      className="my-2 max-w-full rounded-gl border border-border"
    />
  );
}

export function MarkdownView({
  body,
  assets,
  assetsUnavailable = false,
  'data-testid': testId,
}: MarkdownViewProps): React.JSX.Element {
  const components: Components = React.useMemo(
    () => ({
      ...COMPONENTS,
      img: ({ src, alt }) => {
        const raw = typeof src === 'string' ? src : '';
        if (!raw.startsWith(GL_ASSET)) {
          return (
            <img
              src={raw}
              alt={alt ?? ''}
              className="my-2 max-w-full rounded-gl border border-border"
            />
          );
        }
        const slug = raw.slice(GL_ASSET.length);

        // Four distinct states, and collapsing them misleads IT about whether
        // the body is wrong: only one of these means the reference is bad.
        if (assetsUnavailable) {
          return (
            <Placeholder>
              Could not load assets — cannot check <code className="font-mono">{slug}</code>
            </Placeholder>
          );
        }
        if (assets === undefined) return <Placeholder>Resolving {slug}…</Placeholder>;

        const match = assets.find((a) => a.slug === slug);
        if (!match) {
          // Names the slug so IT can see which reference is wrong; docs/32
          // requires a visible placeholder here, never a broken frame.
          return (
            <Placeholder>
              Missing asset: <code className="font-mono">{slug}</code>
            </Placeholder>
          );
        }
        if (!match.previewUrl) {
          return (
            <Placeholder>
              Preview unavailable: <code className="font-mono">{slug}</code>
            </Placeholder>
          );
        }
        return <ResolvedAsset slug={slug} src={match.previewUrl} alt={alt || match.description} />;
      },
    }),
    [assets, assetsUnavailable],
  );

  return (
    <div data-testid={testId} className="break-words text-foreground">
      <ReactMarkdown components={components} urlTransform={urlTransform}>
        {body}
      </ReactMarkdown>
    </div>
  );
}
